'use client';

import { useKeyboardStore } from '@/stores/keyboard';
import { useConvexMutation } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '~/convex/_generated/api';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { Textarea } from './ui/textarea';

type ReviewPromptSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ReviewPromptSheet = ({
  open,
  onOpenChange,
}: ReviewPromptSheetProps) => {
  const { t } = useTranslation();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const submittedRef = useRef(false);

  const { mutate: submitAppReview, isPending: isSubmitting } = useMutation({
    mutationFn: useConvexMutation(api.appReviews.submitAppReview),
  });

  const { mutate: markReviewPromptShown } = useMutation({
    mutationFn: useConvexMutation(api.appReviews.markReviewPromptShown),
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (!submittedRef.current) {
        markReviewPromptShown({}); // Fire-and-forget; don't block close on network
      }
      onOpenChange(false);
      submittedRef.current = false;
      setStars(0);
      setComment('');
    } else {
      onOpenChange(next);
    }
  };

  const handleSubmit = () => {
    if (stars < 1 || stars > 5) return;
    submittedRef.current = true;
    submitAppReview(
      { stars, comment: comment.trim() || undefined },
      {
        onSettled: () => {
          handleOpenChange(false);
        },
      },
    );
  };

  const { keyboardHeight, isOpen: isKeyboardOpen } = useKeyboardStore();

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      direction="bottom"
      repositionInputs={true}>
      <DrawerContent
        className="max-h-[85vh] pb-[calc(var(--safe-area-bottom,0px)+1rem)] transition-[bottom] duration-200"
        title={t('review:title')}
        style={
          isKeyboardOpen && keyboardHeight > 0
            ? { bottom: keyboardHeight }
            : undefined
        }>
        <div className="mx-auto flex w-full max-w-sm flex-col gap-4">
          <DrawerHeader>
            <DrawerTitle className="text-xl">{t('review:title')}</DrawerTitle>
            <DrawerDescription>{t('review:starHint')}</DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-4 px-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-label={`${value} ${value === 1 ? 'star' : 'stars'}`}
                  className="rounded p-1 transition-opacity hover:opacity-80"
                  onClick={() => setStars(value)}>
                  <Star
                    className={`size-7 ${
                      value <= stars
                        ? 'fill-primary text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
            <Textarea
              placeholder={t('review:commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-24 resize-none"
              maxLength={2000}
            />
          </div>
          <DrawerFooter>
            <Button
              onClick={handleSubmit}
              disabled={stars < 1}
              loading={isSubmitting}
              className="w-full">
              {t('review:submit')}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full">
                {t('home:cancel')}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
